import { Observable } from 'rxjs';
import { MyVerbale } from '../models/interfaces/revisione.interface';
import { INDEXED_DB_ID_PREFIX, INFOPATH_HEADER_SIZE, INFOPATH_MAGIC_B64, XSI_NS } from './infopath-attachment.constants';

// ---------------------------------------------------------------------------
// Template-fill export (primary path)
// ---------------------------------------------------------------------------

/**
 * Fills the blank InfoPath XML template with data from MyVerbale.
 *
 * Strategy:
 *  - Walk the template DOM; for each element find the matching MyVerbale key.
 *  - Scalar → set text content (removes xsi:nil when value is present, keeps it when empty).
 *  - Array  → clone the template prototype N times, fill each clone, replace the original.
 *  - Object → recurse into children.
 *  - Keys missing from MyVerbale are left as-is in the template.
 */
export function fillVerbaleTemplate(templateXml: string, verbale: MyVerbale): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(templateXml, 'application/xml');

    const parserError = doc.querySelector('parsererror');
    if (parserError) {
        throw new Error(`Template XML parse error: ${parserError.textContent}`);
    }

    // doc.documentElement is <my:Verbale>; verbale keys are its direct children
    _fillChildren(doc.documentElement, verbale as unknown as Record<string, unknown>);

    return new XMLSerializer().serializeToString(doc);
}

function _fillChildren(templateEl: Element, obj: Record<string, unknown>): void {
    for (const child of Array.from(templateEl.children)) {
        const key = _xmlTagToJsonKey(child);
        if (!(key in obj)) continue;

        const value = obj[key];

        if (Array.isArray(value)) {
            _fillArray(child, value);
        } else if (value !== null && typeof value === 'object') {
            _fillChildren(child, value as Record<string, unknown>);
        } else {
            _fillScalar(child, value as string | number | boolean | null | undefined);
        }
    }
}

function _fillArray(prototype: Element, items: unknown[]): void {
    const parent = prototype.parentElement!;
    const nextSibling = prototype.nextSibling; // preserve original position in schema order
    parent.removeChild(prototype);

    for (const item of items) {
        const clone = prototype.cloneNode(true) as Element;
        if (item !== null && typeof item === 'object' && !Array.isArray(item)) {
            _fillChildren(clone, item as Record<string, unknown>);
        } else {
            _fillScalar(clone, item as string | number | boolean | null | undefined);
        }
        parent.insertBefore(clone, nextSibling);
    }
}

function _fillScalar(el: Element, value: string | number | boolean | null | undefined): void {
    if (value === null || value === undefined || value === '') {
        el.textContent = '';
        // Preserve xsi:nil="true" if the template already has it on this element
        return;
    }
    el.removeAttributeNS(XSI_NS, 'nil');
    // Booleans mirror boolean-checkbox-input's own '0'/'1' convention (see toBoolean), so a
    // never-touched `false` field round-trips through XML import the same as a user-unchecked one.
    if (value === false) {
        el.textContent = '0';
    } else if (value === true) {
        el.textContent = '1';
    } else {
        el.textContent = String(value);
    }
}

/** Converts a namespace-prefixed element name to its MyVerbale JSON key (mirrors toJsonKey in xml-to-verbale.builders.ts). */
function _xmlTagToJsonKey(el: Element): string {
    const localName = el.localName.replace(/-/g, '__');
    return el.prefix ? `${el.prefix}_${localName}` : localName;
}

// ---------------------------------------------------------------------------
// InfoPath attachment encoding (export)
// ---------------------------------------------------------------------------

/**
 * Returns true if the string is already InfoPath-encoded (has the C7 49 46 41 magic header).
 * Use this to skip re-encoding attachments that are already in the correct format.
 */
export function isInfoPathBase64(value: string): boolean {
    return value.startsWith(INFOPATH_MAGIC_B64);
}

/**
 * Builds the InfoPath binary attachment buffer.
 *
 * Layout: [4B magic C7494641][4B headerSize=0x14][4B version=1][4B reserved]
 *         [4B fileSize][4B nameLen (chars incl. null)]
 *         [nameLen*2 B UTF-16LE name incl. null][file bytes]
 */
function _buildInfoPathBuffer(fileData: Uint8Array, fileName: string): Uint8Array {
    const nameLen = fileName.length + 1; // chars including null terminator
    const buf = new Uint8Array(INFOPATH_HEADER_SIZE + nameLen * 2 + fileData.length);
    const view = new DataView(buf.buffer);

    buf[0] = 0xc7;
    buf[1] = 0x49;
    buf[2] = 0x46;
    buf[3] = 0x41; // magic
    view.setUint32(4, 0x14, true); // headerSize
    view.setUint32(8, 1, true); // version
    view.setUint32(12, 0, true); // reserved
    view.setUint32(16, fileData.length, true); // fileSize
    view.setUint32(20, nameLen, true); // nameLen (incl. null)

    // filename in UTF-16LE at offset 24 (null terminator = 0x00 0x00, already zeroed)
    for (let i = 0; i < fileName.length; i++) {
        const code = fileName.charCodeAt(i);
        buf[INFOPATH_HEADER_SIZE + i * 2] = code & 0xff;
        buf[INFOPATH_HEADER_SIZE + i * 2 + 1] = (code >> 8) & 0xff;
    }
    buf.set(fileData, INFOPATH_HEADER_SIZE + nameLen * 2);
    return buf;
}

/** Converts a binary buffer to base64. */
function _bufferToBase64(buf: Uint8Array): string {
    let binary = '';
    for (let i = 0; i < buf.length; i++) binary += String.fromCharCode(buf[i]);
    return btoa(binary);
}

/** Encodes a File into the InfoPath attachment base64 format (reverse of decodeInfoPathAttachment). */
export function encodeFileToInfoPath(file: File): Observable<string> {
    return new Observable<string>((observer) => {
        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const fileData = new Uint8Array(event.target?.result as ArrayBuffer);
                observer.next(_bufferToBase64(_buildInfoPathBuffer(fileData, file.name)));
                observer.complete();
            } catch (err) {
                observer.error(err);
            }
        };
        reader.onerror = () => observer.error(new Error(`Error reading file: ${file.name}`));
        reader.readAsArrayBuffer(file);
        return () => reader.abort();
    });
}

/**
 * Synchronously encodes a raw base64 string into the InfoPath attachment base64 format.
 * Use when the file bytes are already available as base64 (e.g. from the NgRx store).
 */
export function encodeRawBase64ToInfoPath(rawBase64: string, fileName: string): string {
    const binary = atob(rawBase64);
    const fileData = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) fileData[i] = binary.charCodeAt(i);
    return _bufferToBase64(_buildInfoPathBuffer(fileData, fileName));
}

// ---------------------------------------------------------------------------
// IndexedDB ID collection (export pre-processing)
// ---------------------------------------------------------------------------

/** Collects every field whose value is an IndexedDB ID, together with its path in the tree. */
export function collectIndexedDbIds(obj: unknown, path: string[] = []): Array<{ path: string[]; id: string }> {
    if (typeof obj === 'string' && obj.startsWith(INDEXED_DB_ID_PREFIX)) {
        return [{ path, id: obj }];
    }
    if (Array.isArray(obj)) {
        return obj.flatMap((item, i) => collectIndexedDbIds(item, [...path, String(i)]));
    }
    if (obj !== null && typeof obj === 'object') {
        return Object.entries(obj as Record<string, unknown>).flatMap(([key, val]) => collectIndexedDbIds(val, [...path, key]));
    }
    return [];
}

// ---------------------------------------------------------------------------
// Attachment placeholder fill (export defensive pass)
// ---------------------------------------------------------------------------

/**
 * Every MyVerbale field that carries an InfoPath file attachment.
 * In the NgRx store these can hold:
 *   - an IndexedDB ID (file uploaded via the UI / replaced during import)
 *   - a raw base64 string (e.g. Bilancio XBRL coming from the API)
 *   - an already-encoded InfoPath base64 string (real file retrieved from IndexedDB)
 *   - empty (nothing was ever uploaded)
 */
const ATTACHMENT_FIELD_NAMES = new Set([
    'my_AltroAllegatoElement',
    'my_AltroAllegatoAccertamentoElement',
    'my_BilancioAllegato',
    'my_NotaIntegrativa',
    'my_CopiaBilancio',
    'my_RelazioneCertificazione',
    'my_ContrattoCertificazione',
    'my_FileStatuto',
    'my_DichiarazioneSostitutiva',
    'my_DiffidaMinistero',
    'my_AllegatoVisura',
    'my_AllegatoCopiaBiancioAccertamento',
    'my_RaccomandataDiffidaFareAccertamento',
]);

/**
 * Recursively walks the verbale clone: when an attachment field carries a file
 * (any non-empty value) it is replaced with the placeholder InfoPath payload, unless
 * the value is already a valid InfoPath base64 (e.g. the real Bilancio retrieved from
 * IndexedDB) — in that case it is preserved as-is.
 *
 * Empty values are left empty so the template's `xsi:nil="true"` is preserved;
 * InfoPath rejects file fields holding non-InfoPath data but accepts truly nil ones.
 *
 * Mutates `obj` in place.
 */
export function fillAttachmentPlaceholders(obj: unknown, placeholder: string): void {
    if (Array.isArray(obj)) {
        for (const item of obj) fillAttachmentPlaceholders(item, placeholder);
        return;
    }
    if (obj === null || typeof obj !== 'object') return;

    const record = obj as Record<string, unknown>;
    for (const key of Object.keys(record)) {
        const value = record[key];
        if (ATTACHMENT_FIELD_NAMES.has(key)) {
            if (typeof value === 'string' && value !== '' && !isInfoPathBase64(value)) {
                record[key] = placeholder;
            }
        } else {
            fillAttachmentPlaceholders(value, placeholder);
        }
    }
}
